import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { createSupport, validateSupportAttachments, MAX_SUPPORT_ATTACHMENTS } from "../../../services/SupportService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  SUPPORT_TYPES,
  SUPPORT_PRIORITIES,
  SUPPORT_ISSUE_STARTED_OPTIONS,
  SUPPORT_CONTACT_METHODS,
  getSupportSubcategories,
  typeHasSubcategories,
} from "../../../contants/supportTypes";
import "./SupportFloatingButton.css";

const INITIAL_FORM = {
  type: "",
  subcategory: "",
  customSubcategory: "",
  customType: "",
  priority: "moyenne",
  issueStarted: "today",
  preferredContactMethod: "email",
  description: "",
  recommendation: "",
};

const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

const SupportFloatingButton = () => {
  const fileInputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const openFromEvent = () => setShowModal(true);
    window.addEventListener("open-support-modal", openFromEvent);
    return () => window.removeEventListener("open-support-modal", openFromEvent);
  }, []);

  const subcategories = useMemo(
    () => getSupportSubcategories(formData.type),
    [formData.type]
  );

  const showSubcategoryField =
    Boolean(formData.type) && typeHasSubcategories(formData.type);

  const handleTypeChange = (nextType) => {
    setFormData({
      ...formData,
      type: nextType,
      subcategory: "",
      customSubcategory: "",
      customType: nextType !== "other" ? "" : formData.customType,
    });
  };

  const handleAttachmentsChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    e.target.value = "";

    if (!newFiles.length) return;

    const merged = [...attachments];
    for (const file of newFiles) {
      const key = getFileKey(file);
      if (merged.some((existing) => getFileKey(existing) === key)) continue;
      merged.push(file);
    }

    const error = validateSupportAttachments(merged);
    if (error) {
      toast.error(error);
      return;
    }

    setAttachments(merged);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const canAddMoreAttachments = attachments.length < MAX_SUPPORT_ATTACHMENTS;

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setAttachments([]);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const showSuccessMessage = () => {
    Swal.fire({
      icon: "success",
      title: "Request received",
      html: `
        <p style="margin:0 0 12px;line-height:1.6;color:#475569;">
          Thank you for contacting our support team. Your request has been successfully submitted
          and is now being reviewed.
        </p>
        <p style="margin:0;line-height:1.6;color:#475569;">
          We will get back to you within <strong>24 hours</strong> using your preferred contact method.
          If your situation is critical, our team will prioritize your request accordingly.
        </p>
      `,
      confirmButtonText: "Understood",
      confirmButtonColor: "#fc8575",
      customClass: {
        popup: "support-success-popup",
        title: "support-success-popup__title",
        htmlContainer: "support-success-popup__body",
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      toast.error("Please provide a description");
      return;
    }

    if (!formData.type) {
      toast.error("Please select a type");
      return;
    }

    if (formData.type === "other" && !formData.customType.trim()) {
      toast.error("Please provide a custom type");
      return;
    }

    if (showSubcategoryField && !formData.subcategory) {
      toast.error("Please select a subcategory");
      return;
    }

    if (formData.subcategory === "other" && !formData.customSubcategory.trim()) {
      toast.error("Please specify the subcategory");
      return;
    }

    setLoading(true);
    try {
      await createSupport({
        type: formData.type,
        description: formData.description,
        recommendation: formData.recommendation,
        customType: formData.customType,
        subcategory: formData.subcategory,
        customSubcategory: formData.customSubcategory,
        priority: formData.priority,
        issueStarted: formData.issueStarted,
        preferredContactMethod: formData.preferredContactMethod,
        attachments,
      });
      closeModal();
      showSuccessMessage();
      window.dispatchEvent(new CustomEvent("support-created"));
    } catch (error) {
      toast.error(error.message || "Failed to submit support request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="support-fab"
        onClick={() => setShowModal(true)}
        title="Support"
        aria-label="Open support form"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" />
          <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" />
        </svg>
      </button>

      <Modal
        show={showModal}
        onHide={closeModal}
        size="lg"
        centered
        className="support-modal"
        backdropClassName="support-modal-backdrop"
        dialogClassName="support-modal__dialog"
        contentClassName="support-modal__content"
      >
        <div className="support-modal__header">
          <div className="support-modal__header-text">
            <span className="support-modal__header-icon" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" />
                <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" />
              </svg>
            </span>
            <h2 className="support-modal__title">Support request</h2>
          </div>
          <button
            type="button"
            className="support-modal__close"
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="support-modal__body">
            <section className="support-modal__section">
              <h3 className="support-modal__section-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M4 6h16M4 12h10M4 18h7" />
                </svg>
                Request type
              </h3>

              <div className="support-field">
                <label className="support-field__label" htmlFor="support-type">
                  Type
                </label>
                <Form.Select
                  id="support-type"
                  value={formData.type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Select a type
                  </option>
                  {SUPPORT_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.emoji} {item.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {showSubcategoryField ? (
                <div className="support-field">
                  <label className="support-field__label" htmlFor="support-subcategory">
                    Subcategory
                  </label>
                  <Form.Select
                    id="support-subcategory"
                    value={formData.subcategory}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subcategory: e.target.value,
                        customSubcategory:
                          e.target.value !== "other" ? "" : formData.customSubcategory,
                      })
                    }
                    required
                  >
                    <option value="" disabled>
                      Select a subcategory
                    </option>
                    {subcategories.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ) : null}

              {showSubcategoryField && formData.subcategory === "other" ? (
                <div className="support-field">
                  <label className="support-field__label" htmlFor="support-custom-subcategory">
                    Custom subcategory *
                  </label>
                  <Form.Control
                    id="support-custom-subcategory"
                    type="text"
                    value={formData.customSubcategory}
                    onChange={(e) =>
                      setFormData({ ...formData, customSubcategory: e.target.value })
                    }
                    placeholder="Please specify the subcategory..."
                    required
                  />
                </div>
              ) : null}

              {formData.type === "other" ? (
                <div className="support-field">
                  <label className="support-field__label" htmlFor="support-custom-type">
                    Custom type *
                  </label>
                  <Form.Control
                    id="support-custom-type"
                    type="text"
                    value={formData.customType}
                    onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                    placeholder="Please specify the type..."
                    required
                  />
                </div>
              ) : null}
            </section>

            <section className="support-modal__section">
              <h3 className="support-modal__section-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
                Priority &amp; contact
              </h3>

              <div className="support-field">
                <span className="support-field__label">Priority</span>
                <div className="support-chip-group" role="group" aria-label="Priority">
                  {SUPPORT_PRIORITIES.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={`support-chip${
                        formData.priority === item.value ? " is-active" : ""
                      }`}
                      onClick={() => setFormData({ ...formData, priority: item.value })}
                    >
                      {item.emoji} {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="support-modal__grid">
                <div className="support-field">
                  <label className="support-field__label" htmlFor="support-issue-started">
                    When did the issue start?
                  </label>
                  <Form.Select
                    id="support-issue-started"
                    value={formData.issueStarted}
                    onChange={(e) => setFormData({ ...formData, issueStarted: e.target.value })}
                    required
                  >
                    {SUPPORT_ISSUE_STARTED_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="support-field">
                  <label className="support-field__label" htmlFor="support-contact-method">
                    Preferred contact method
                  </label>
                  <Form.Select
                    id="support-contact-method"
                    value={formData.preferredContactMethod}
                    onChange={(e) =>
                      setFormData({ ...formData, preferredContactMethod: e.target.value })
                    }
                    required
                  >
                    {SUPPORT_CONTACT_METHODS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.emoji} {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </section>

            <section className="support-modal__section">
              <h3 className="support-modal__section-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M4 5h16v14H4z" />
                  <path d="M8 9h8M8 13h5" />
                </svg>
                Details
              </h3>

              <div className="support-field">
                <label className="support-field__label" htmlFor="support-description">
                  Description *
                </label>
                <Form.Control
                  id="support-description"
                  className="support-field__textarea--description"
                  as="textarea"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your issue or feedback in detail..."
                  required
                />
              </div>

              <div className="support-field">
                <span className="support-field__label">Pièces jointes</span>
                {canAddMoreAttachments ? (
                  <div className="support-upload">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,application/pdf"
                      onChange={handleAttachmentsChange}
                      aria-label="Upload attachments"
                    />
                    <div className="support-upload__icon" aria-hidden>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 16V4m0 0 8-4m-4 4 4-4" />
                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                      </svg>
                    </div>
                    <div className="support-upload__text">
                      <span className="support-upload__title">
                        {attachments.length > 0 ? "Add more files" : "Click to add files"}
                      </span>
                      <span className="support-upload__hint">
                        PNG, JPG, WEBP, GIF or PDF · {attachments.length}/{MAX_SUPPORT_ATTACHMENTS} files
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="support-upload__limit">
                    Maximum of {MAX_SUPPORT_ATTACHMENTS} files reached. Remove one to add another.
                  </p>
                )}
                {attachments.length > 0 ? (
                  <ul className="support-attachment-list">
                    {attachments.map((file, index) => (
                      <li key={getFileKey(file)} className="support-attachment-item">
                        <span className="support-attachment-item__icon" aria-hidden>
                          {file.type === "application/pdf" ? "📄" : "🖼️"}
                        </span>
                        <span className="support-attachment-item__name">{file.name}</span>
                        <button
                          type="button"
                          className="support-attachment-item__remove"
                          onClick={() => removeAttachment(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="support-field">
                <label className="support-field__label" htmlFor="support-recommendation">
                  Recommendation <span className="text-muted">(optional)</span>
                </label>
                <Form.Control
                  id="support-recommendation"
                  className="support-field__textarea--compact"
                  as="textarea"
                  rows={2}
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                  placeholder="Any suggestions to improve the experience..."
                />
              </div>
            </section>
          </Modal.Body>

          <Modal.Footer className="support-modal__footer">
            <button type="button" className="support-btn support-btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="support-btn support-btn--primary" disabled={loading}>
              {loading ? "Submitting…" : "Submit request"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SupportFloatingButton;
